import React from 'react'
import {
  Button,
  Container,
  TextField,
  Checkbox,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material'
import { useForm, Controller, FormProvider } from 'react-hook-form'
import type { FieldValues } from 'react-hook-form'
import { Layout } from 'ui/layout/Layout'
import { AppBarIhAdmin } from 'ui/partials/AppBar'
import { StackItem } from 'ui/layout/StackItem'
import { H2, Text1, Text2 } from 'ui/shorthands/Texts'
import QuestionTooltip from 'ui/shorthands/QuestionTooltip'
import {
  StackItemTextField,
  useStyles as textFieldStyles,
} from 'ui/shorthands/StackItemTextField'
import placeholder from 'config/placeholder'
import {
  applicationEntryStyles,
  applicationEntryRegularValidationSchema,
  applicationEntryPartnerValidationSchema,
} from '../med/ApplicationEntry/ApplicationEntry'
import { makeStyles } from 'tss-react/mui'
import type {
  ApplicationEntryEditFormProps,
  AdminMedicalInstitution,
} from 'types/med'
import { partnerCompanyProps } from 'types/med'
import FormHelperText from '@mui/material/FormHelperText'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import validationSchema from 'ui/shorthands/validationSchema'
import { SchemaOf } from 'yup'
import MarginRightCancelButton from 'ui/shorthands/MarginRightCancelButton'
import MedAddressInputForm from 'ui/organisms/MedAddressInputForm'
import { ApiErrorWithMessage } from 'app/client'

const useStyles = makeStyles()(() => ({
  control: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}))

export type MedicalInstitutionLEditProps = {
  partnerCompanies: Array<partnerCompanyProps>
  defaultValues: AdminMedicalInstitution
  onSubmit: (data: ApplicationEntryEditFormProps) => Promise<void>
  onClickCancel: () => void
  onClickDelete?: () => Promise<void> // NOTE:削除APIはあるがまだ削除機能についてドキュメントにないため未実装
}

// NOTE: partner_company_idの有無でschemaを切り替える
const whenHasPartnerId = (schema: SchemaOf<any>) =>
  yup
    .object()
    .when('partner_company_id', (partner_company_id: number | null) =>
      typeof partner_company_id === 'number' ? schema : yup.string().nullable()
    )

const MedicalInstitutionLEdit: React.FC<MedicalInstitutionLEditProps> = ({
  partnerCompanies,
  defaultValues,
  onSubmit,
  onClickCancel,
}: MedicalInstitutionLEditProps) => {
  const { classes } = applicationEntryStyles()
  const textFieldClasses = textFieldStyles().classes
  const { classes: localClasses, cx } = useStyles()

  const schema = yup.object().shape({
    ...applicationEntryRegularValidationSchema(),
    plan_category: yup.object().nullable(),
    plan: yup.object().nullable(),
    campaign_code: yup.object().nullable(),
    initial_support: yup.object().nullable(),
    partner: yup.object().shape({
      ...applicationEntryPartnerValidationSchema(),
      ...{
        partner_company_id: validationSchema
          .numberSelectEmptyStringToNull()
          .nullable(),
        contact_person_name: whenHasPartnerId(validationSchema.textRequired()),
        email: whenHasPartnerId(validationSchema.email()),
        phone: whenHasPartnerId(validationSchema.phone().required()),
      },
    }),
  })

  const formMethods = useForm<FieldValues>({
    defaultValues,
    resolver: yupResolver(schema),
    mode: 'onChange',
  })
  const {
    handleSubmit,
    control,
    setError,
    formState: { isValid, isDirty, errors },
  } = formMethods

  const submitHandler = (data: FieldValues) => {
    const newData = { ...data }
    if (!data.partner?.partner_company_id) {
      delete newData['partner']
    }
    onSubmit(newData as ApplicationEntryEditFormProps).catch((e) => {
      if (
        e instanceof ApiErrorWithMessage &&
        e.has({
          type: 'validation_error',
          key: 'admin_user.email',
          value: 'unique',
        })
      ) {
        setError('admin_user.email', {
          type: 'unique',
          message: 'このメールアドレスはすでに登録されています',
        })
      }
    })
  }

  const sortedPartnerCompanies = partnerCompanies
    ? [...partnerCompanies].sort((a, b) => {
        const aName = a.name
        const bName = b.name
        if (aName < bName) {
          return -1
        }
        if (aName > bName) {
          return 1
        }
        return 0
      })
    : undefined

  return (
    <form onSubmit={handleSubmit(submitHandler)}>
      <Layout
        header={
          <AppBarIhAdmin>
            YaDoc Quick 管理 : 「{defaultValues?.name}」
          </AppBarIhAdmin>
        }
      >
        <Container maxWidth="md">
          <H2 className={classes.header}>施設</H2>
          <div className={classes.category}>
            <StackItem label={'レコードID'}>
              <Text2>{defaultValues?.id}</Text2>
              <Controller
                name={'id'}
                render={({ field }) => <input {...field} type="hidden" />}
                control={control}
              />
            </StackItem>
            <StackItemTextField
              name="name"
              label={'施設名'}
              placeholder={placeholder().clinic_name}
              errorMessage={errors?.name?.message}
              control={control}
              fullWidth
              required
            />
            <StackItemTextField
              name="phone"
              label={'電話番号'}
              placeholder={placeholder().phone_number}
              errorMessage={errors?.phone?.message}
              control={control}
              fullWidth
              required
            />
            <FormProvider {...formMethods}>
              <MedAddressInputForm />
            </FormProvider>
            <StackItemTextField
              name="introduced_partner"
              label={'紹介代理店'}
              errorMessage={errors?.introduced_partner?.message}
              control={control}
              fullWidth
            />
            <StackItem
              label={
                <>
                  YaDocのご利用
                  <QuestionTooltip>
                    既に「オンライン診療システムYaDoc」のアカウントをお持ちの方は、チェックを入れてください。
                  </QuestionTooltip>
                </>
              }
            >
              <Text2 className={classes.text}>
                <Controller
                  name={'is_applied_yadoc'}
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      {...field}
                      checked={!!field.value}
                      className={classes.topCheckbox}
                      color="primary"
                    />
                  )}
                />
                YaDocアカウントあり
              </Text2>
            </StackItem>
            <StackItem label={'フォームURL'}>
              <Text2>{defaultValues?.form_url}</Text2>
            </StackItem>
            <StackItem label={'医療機関別URL'}>
              <Text2>
                {defaultValues?.online_eligibility_verification_url}
              </Text2>
            </StackItem>
          </div>
          <H2 className={classes.header}>管理者</H2>
          <div className={classes.category}>
            <StackItem label={'レコードID'}>
              <Text2>{defaultValues?.admin_user?.id}</Text2>
              <Controller
                name={'admin_user.id'}
                render={({ field }) => <input {...field} type="hidden" />}
                control={control}
              />
            </StackItem>
            <StackItem label={'氏名'} required>
              <span className={classes.firstInput}>
                <Controller
                  name={'admin_user.last_name'}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      variant="outlined"
                      placeholder={placeholder().last_name}
                      error={!!errors?.admin_user?.last_name}
                      helperText={errors?.admin_user?.last_name?.message}
                      InputProps={{
                        className: cx(
                          !field?.value && textFieldClasses.required
                        ),
                      }}
                    />
                  )}
                />
              </span>
              <Controller
                name={'admin_user.first_name'}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    variant="outlined"
                    placeholder={placeholder().first_name}
                    error={!!errors?.admin_user?.first_name}
                    helperText={errors?.admin_user?.first_name?.message}
                    InputProps={{
                      className: cx(!field?.value && textFieldClasses.required),
                    }}
                  />
                )}
              />
            </StackItem>
            <StackItem label={'氏名(ふりがな)'} required>
              <span className={classes.firstInput}>
                <Controller
                  name={'admin_user.last_name_kana'}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      variant="outlined"
                      placeholder={placeholder().last_name_kana}
                      error={!!errors?.admin_user?.last_name_kana}
                      helperText={errors?.admin_user?.last_name_kana?.message}
                      InputProps={{
                        className: cx(
                          !field?.value && textFieldClasses.required
                        ),
                      }}
                    />
                  )}
                />
              </span>
              <Controller
                name={'admin_user.first_name_kana'}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    variant="outlined"
                    placeholder={placeholder().first_name_kana}
                    error={!!errors?.admin_user?.first_name_kana}
                    helperText={errors?.admin_user?.first_name_kana?.message}
                    InputProps={{
                      className: cx(!field?.value && textFieldClasses.required),
                    }}
                  />
                )}
              />
            </StackItem>
            <StackItemTextField
              name="admin_user.email"
              label={'メールアドレス'}
              placeholder={placeholder().email}
              description={'ログイン、予約の通知、請求の連絡などに使用します。'}
              errorMessage={errors?.admin_user?.email?.message}
              control={control}
              alignTop
              fullWidth
              required
            />
          </div>
          <H2 className={classes.header}>代理店</H2>
          <div className={classes.category}>
            {!partnerCompanies ? (
              <StackItemTextField
                name="introduced_partner"
                label={'紹介代理店名'}
                errorMessage={errors?.introduced_partner?.message}
                control={control}
                fullWidth
              />
            ) : (
              <>
                <StackItem label={'企業名'}>
                  <FormControl
                    variant="outlined"
                    className={classes.formControl}
                    error={!!errors?.partner?.partner_company_id}
                  >
                    <Controller
                      name={'partner.partner_company_id'}
                      control={control}
                      render={({ field }) => (
                        <Select {...field} variant="outlined">
                          <MenuItem>選択してください</MenuItem>
                          {sortedPartnerCompanies?.map((o) => (
                            <MenuItem key={`${o.name}${o.id}`} value={o.id}>
                              {o.name}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                    {errors?.partner?.partner_company_id && (
                      <FormHelperText>
                        {errors?.partner?.partner_company_id?.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                </StackItem>
                <StackItemTextField
                  name="partner.branch_name"
                  label={'部署・支店名(任意)'}
                  placeholder={placeholder().partner_branch_name}
                  errorMessage={errors?.partner?.branch_name?.message}
                  control={control}
                  fullWidth
                />
                <StackItemTextField
                  name="partner.contact_person_name"
                  label={'担当者名'}
                  placeholder={placeholder().account_name}
                  errorMessage={errors?.partner?.contact_person_name?.message}
                  control={control}
                  fullWidth
                />
                <StackItemTextField
                  name="partner.email"
                  label={'メールアドレス'}
                  placeholder={placeholder().email}
                  errorMessage={errors?.partner?.email?.message}
                  control={control}
                  fullWidth
                />
                <StackItemTextField
                  name="partner.phone"
                  label={'電話番号'}
                  placeholder={placeholder().phone_number}
                  errorMessage={errors?.partner?.phone?.message}
                  control={control}
                  fullWidth
                />
                <StackItemTextField
                  name="partner.remarks"
                  label={'備考(任意)'}
                  errorMessage={errors?.partner?.remarks?.message}
                  control={control}
                  alignTop
                  fullWidth
                  multiline
                />
              </>
            )}
            <Controller
              name={'plan'}
              render={({ field }) => <input {...field} type="hidden" />}
              control={control}
            />
            <Controller
              name={'plan_category'}
              render={({ field }) => <input {...field} type="hidden" />}
              control={control}
            />
            <Controller
              name={'campaign_code'}
              render={({ field }) => <input {...field} type="hidden" />}
              control={control}
            />
          </div>

          <H2 className={classes.header}>ステータス</H2>
          <div className={classes.category}>
            <StackItem label={'利用状態'}>
              <Text1 className={classes.text}>
                <Controller
                  name={'is_active'}
                  render={({ field }) => (
                    <Checkbox
                      {...field}
                      checked={!!field.value}
                      className={classes.topCheckbox}
                      color="primary"
                    />
                  )}
                  control={control}
                />
                利用しない場合はチェックを外す
              </Text1>
            </StackItem>
          </div>
          <div className={cx(classes.submitWrapper, localClasses.control)}>
            <MarginRightCancelButton onClick={onClickCancel} />
            <Button
              className={cx(classes.submitButton)}
              variant="contained"
              color="primary"
              type="submit"
              disabled={!isValid || !isDirty}
            >
              保存
            </Button>
          </div>
        </Container>
      </Layout>
    </form>
  )
}

export default MedicalInstitutionLEdit
